import { Skeleton } from 'components/Skeleton/Skeleton'
import { PreviewWithLayerSnapshots, ProsePreview } from 'common/ProsePreview/ProsePreview'
import { useArticle } from 'hooks/queries/articles/useArticle'
import { schema } from 'prose-core'
import { MarkType, Node, Schema } from 'prosemirror-model'
import { type EditorView } from 'prosemirror-view'
import { useMemo } from 'react'
import { ArticleFlat } from 'types'
import Styles from './ArticlePreview.module.scss'

type ArticleMarks = { [p: string]: MarkType<Schema<string, string>> }

export interface ArticlePreviewProps {
  articleId: string
  onPreviewInit?: (view: EditorView) => void
  removeMarksConfigurer?: (marks: ArticleMarks) => MarkType[]
  withLayerSnapshots?: boolean
}

const prepareNodes = (elements: any[], schema: Schema) => {
  return elements.map((node) => {
    return Node.fromJSON(schema, node.data_json)
  })
}

export const ArticlePreview = ({
  articleId,
  onPreviewInit,
  removeMarksConfigurer,
  withLayerSnapshots = false,
}: ArticlePreviewProps) => {
  const articleQuery = useArticle(articleId, {})
  const article = articleQuery.data?.article as ArticleFlat

  const removeMarks = useMemo(() => {
    const articleRemoveMarks = removeMarksConfigurer && removeMarksConfigurer(schema.marks)
    return articleRemoveMarks
  }, [removeMarksConfigurer])

  return (
    <div className={Styles.preview} data-testid="article-preview">
      {articleId === '' && <></>}
      {articleQuery.isLoading && <Skeleton />}
      {articleQuery.isError && <div className={Styles.errorState}>Preview not available. Try refreshing the page.</div>}
      {articleQuery.isSuccess &&
        (withLayerSnapshots ? (
          <PreviewWithLayerSnapshots
            articleStyles={[article.workspace_article_style, article.article_style]}
            schema={schema}
            onInit={onPreviewInit}
            removeMarks={removeMarks}
            layer={article.main_layer}
          />
        ) : (
          <ProsePreview
            articleStyles={[article.workspace_article_style, article.article_style]}
            schema={schema}
            nodes={prepareNodes(article.elements, schema)}
            onInit={onPreviewInit}
            removeMarks={removeMarks}
          />
        ))}
    </div>
  )
}
